import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Image } from "lucide-react";
import api from "../lib/api";
import useSEO from "../lib/useSEO";

export default function Gallery() {
    useSEO({
        title: "Gallery",
        description: "Browse the firm gallery of curated imagery showcasing Rotimi Jacobs & Co.",
        path: "/gallery",
    });

    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/gallery")
            .then(({ data }) => setImages(data))
            .catch(() => setImages([]))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div data-testid="page-gallery">
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
                <div className="font-mono-cap text-xs text-[#c5a059] mb-3 flex items-center gap-2">
                    <Image className="w-3.5 h-3.5" /> Gallery
                </div>
                <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl max-w-3xl leading-tight">
                    A visual record of the chamber’s work and presence.
                </h1>
                <p className="mt-6 max-w-2xl text-slate-800 leading-relaxed text-[17px]">
                    Discover curated imagery from Rotimi Jacobs & Co, including firm events, editorial visuals and law practice portraits.
                </p>
                <div className="mt-10">
                    <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 text-sm font-medium text-[#0F172A] bg-[#c5a059] px-5 py-3 rounded-sm hover:bg-[#faf9f6] transition-colors"
                        data-testid="gallery-contact-button"
                    >
                        Book consultation
                    </Link>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
                {loading ? (
                    <div className="font-mono-cap text-xs text-slate-500">Loading…</div>
                ) : images.length === 0 ? (
                    <div className="text-slate-600">No gallery images are available at this time.</div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {images.map((image) => (
                            <div key={image.id} className="group overflow-hidden rounded-sm border border-slate-900/10 bg-white shadow-sm">
                                <div className="aspect-[4/3] bg-slate-100">
                                    <img src={image.public_url} alt={image.original_filename || "Gallery image"} className="w-full h-full object-cover transition duration-300 group-hover:scale-105" />
                                </div>
                                <div className="p-4">
                                    <div className="font-serif-display text-lg text-slate-900">{image.original_filename || "Image"}</div>
                                    <div className="text-xs text-slate-500 mt-2">Uploaded {new Date(image.created_at).toLocaleDateString()}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
